import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// import { useAuth, api } from '../context/AuthContext';
import api  from '../api';

const CreditDetail = ({ fetchCredits }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [credit, setCredit] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [customerInfo, setCustomerInfo] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchCredit = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`/credits/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCredit(response.data);
        const customerData = typeof response.data.customer === 'string' ? 
          { name: response.data.customer } : 
          response.data.customer || {};
        setCustomerInfo(customerData);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Credit not found');
        setLoading(false);
      }
    };
    fetchCredit();
  }, [id]);

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const numericAmount = parseFloat(paymentAmount);
      
      if (numericAmount <= 0 || numericAmount > safeRemaining) {
        setError('Invalid payment amount');
        return;
      }
  
      // Process payment
      await api.post(`/credits/${id}/pay`, { 
        amount: numericAmount.toFixed(2),
        method: paymentMethod 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
  
      // Refresh data
      const updatedCredit = await api.get(`/credits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCredit(updatedCredit.data);
      setPaymentAmount('');
      setError('');
      
      if (fetchCredits) fetchCredits();
      
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed');
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await api.patch(`/credits/${id}`, { customer: customerInfo }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedCredit = await api.get(`/credits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredit(updatedCredit.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    }
  };

  if (loading) return <div className="container mt-4">Loading...</div>;
  if (error) return <div className="container mt-4 alert alert-danger">{error}</div>;
  if (!credit) return <div className="container mt-4">Credit not found</div>;

  const safeTotal = Number(credit.totalAmount?.toString()) || 0;
  const safeRemaining = Number(credit.remainingBalance?.toString()) || 0;
  const safePaid = safeTotal - safeRemaining;
  const paymentHistory = credit.payments || [];

  return (
    <div className="container mt-4">
      <button onClick={() => navigate('/credits')} className="btn btn-link mb-4">
        &larr; Back to Credits List
      </button>

      <div className="card shadow">
        <div className="card-body">
          <h3 className="mb-4">
            Credit Details - {credit.customer?.name || 'Unknown Customer'}
            <span className={`badge ${credit.status === 'paid' ? 'bg-success' : 'bg-warning'} ms-2`}>
              {credit.status}
            </span>
          </h3>

          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card">
                <div className="card-body">
                  <h5>Amount Paid</h5>
                  <p className="h4 text-success">${safePaid.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card">
                <div className="card-body">
                  <h5>Balance Remaining</h5>
                  <p className="h4 text-danger">${safeRemaining.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="card mb-4">
                <div className="card-body">
                  <h4 className="mb-3">Make Payment</h4>
                  <form onSubmit={handlePayment}>
                    <div className="mb-3">
                      <label className="form-label">Payment Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        required
                        min="0.01"
                        max={safeRemaining}
                      />
                      <small className="form-text text-muted">
                        Maximum payment: ${safeRemaining.toFixed(2)}
                      </small>
                    </div>
                    <button type="submit" className="btn btn-success">
                      Process Payment
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card mb-4">
                <div className="card-body">
                  <h4 className="mb-3">Customer Information</h4>
                  <form onSubmit={handleUpdateCustomer}>
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={customerInfo.name || ''}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={customerInfo.email || ''}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={customerInfo.phone || ''}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary">
                      Update Information
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h4 className="mb-3">Payment History</h4>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment, index) => (
                      <tr key={index}>
                        <td>{new Date(payment.paymentDate).toLocaleString()}</td>
                        <td>${(Number(payment.amount?.toString()).toFixed(2) || '0.00')}</td>
                        <td>{payment.method || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CreditDetail;
